import { apiClient } from './client';
import type { ResourceOrigin } from '@/types/planetDashboard';

/**
 * Lot 35D — Profil joueur + parties actives. La home view interroge ce
 * endpoint juste apres login pour decider sur quoi rediriger (planet
 * courante vs lobby).
 */

const BASE = '/api/v1/me';

export interface PlayerProfile {
  id: number;
  login: string;
  email: string;
  avatarPath: string | null;
  locale: string;
}

export interface ActiveGameDto {
  playerUniverseId: number;
  universeId: number;
  universeName: string;
  raceId: number;
  raceLabel: string;
  currentPlanetId: number | null;
  /** Capitale choisie (PlayerUniverse.CapitalPlanetId). Null = plus ancienne planète. */
  capitalPlanetId: number | null;
  level: number;
  lastLoginAt: string | null;
  /** God-mode (omniscience) actif : le front n'assombrit plus les planètes non possédées. */
  isGodMode?: boolean;
  /** Nombre total de planètes de l'univers de cette partie. */
  planetCount: number;
  /** Nombre de planètes que CE joueur a découvertes (jauge d'exploration personnelle). */
  exploredPlanets: number;
}

export interface MeDto {
  player: PlayerProfile;
  games: ActiveGameDto[];
}

export async function fetchMe(): Promise<MeDto> {
  const { data } = await apiClient.get<MeDto>(BASE);
  return data;
}

// --- Capitale (PlayerUniverse.CapitalPlanetId) : choix + cooldown + coût -----

export interface CapitalStatus {
  playerUniverseId: number;
  capitalPlanetId: number | null;
  capitalChangeCount: number;
  capitalChangedAt: string | null;
  /** Coût flouz du prochain changement (échelonné). */
  nextCost: number;
  canChangeNow: boolean;
  /** Date à partir de laquelle un changement est de nouveau possible (null si maintenant). */
  nextChangeAt: string | null;
}

export interface CapitalActionResult {
  success: boolean;
  errorCode: string | null;
  message: string | null;
  status: CapitalStatus | null;
}

/** État capitale pour le PU du joueur dans l'univers de cette planète. */
export async function fetchCapitalStatus(planetId: number): Promise<CapitalStatus> {
  const { data } = await apiClient.get<CapitalStatus>(`${BASE}/capital`, { params: { planetId } });
  return data;
}

/** Définit la planète comme capitale (débit flouz échelonné + cooldown 72h). */
export async function setCapital(planetId: number): Promise<CapitalActionResult> {
  const { data } = await apiClient.post<CapitalActionResult>(`${BASE}/capital`, { planetId });
  return data;
}

/** Categorie de gestion d'une ressource, miroir de EResourceCategory backend. */
export type ResourceCategory = 'Consommable' | 'Flux' | 'Population';

export interface ResourceSummaryEntry {
  resourceId: number;
  code: string;
  label: string;
  available: number;
  productionPerHour: number;
  /**
   * Capacite max de stockage sur la planete (silos + entrepots).
   * 0 = pas de limite V1.
   */
  stored: number;
  /** Categorie de gestion (Consommable / Flux / Population). */
  category: ResourceCategory;
  /** Origine / rôle économique (Primaire/Fabriquee/Precieuse/Vitale/Devise/Energie).
   *  Pilote le regroupement des stocks dans le header. */
  origin?: ResourceOrigin;
  /** Prix unitaire de référence (origine + rareté) — repère pour fixer un prix
   *  d'annonce dans l'UI de vente. */
  suggestedUnitPrice?: number;

  // --- Spécifique à l'entrée Population (habitants) ; absent ailleurs. ---
  /** Habitants blessés en convalescence (soignés par l'Hôpital au tick). */
  woundedPopulation?: number;
  /** Main-d'œuvre requise par les bâtiments actifs (Σ Level × LaborBase). */
  laborUsed?: number;
  /** Main-d'œuvre disponible (population − équipage embarqué). */
  laborAvailable?: number;
  /** Ratio de production effectif lié à la main-d'œuvre (0..100). 100 = suffisant. */
  laborRatioPct?: number;
}

export interface ResourceSummaryDto {
  /** Nombre total de planètes du joueur (info, pas pour calcul). */
  planetCount: number;
  /** Id de la planète dont les ressources sont retournées (null si aucune planète). */
  planetId: number | null;
  /** Nom de la planète dont les ressources sont retournées. */
  planetName: string | null;
  /** Stocks et production de la planète indiquée par planetId. */
  resources: ResourceSummaryEntry[];
  /**
   * Solde flouz du joueur (PlayerUniverse.Solde agrege) = PORTEFEUILLE.
   * C'est ce solde qui est debite lors d'un ship/research/build start.
   * Distinct de bankDeposits (qui dort a la banque, ne sert qu'a faire
   * des interets et n'est PAS utilisable pour les constructions).
   */
  playerFlouz: number;
  /**
   * Somme des Planet.BankDeposit sur les planetes du joueur. Cet argent
   * dort -- il genere des interets au tick (BankService.AccrueAllAsync)
   * mais n'est PAS dans le portefeuille. Le tooltip flouz l'affiche pour
   * la vue d'ensemble du patrimoine.
   */
  bankDeposits: number;
  /** Prets banque actifs du joueur (vide si aucun). */
  activeLoans: ActiveLoanSummary[];
  /** Emplacements de bâtiments occupés sur la planète affichée (Σ Places non-QG). */
  placesUsed: number;
  /** Capacité totale d'emplacements de la planète affichée (Planet.Places). */
  placesTotal: number;
  /** Niveau courant du joueur (partie active), dérivé du TotalScore. */
  level: number;
  /** Score total = PointBattle + PointResearch + PointConstruction (partie active). */
  totalScore: number;
  /** Score requis pour atteindre le niveau courant (borne basse de la barre). */
  levelStartScore: number;
  /** Score requis pour le niveau suivant (borne haute de la barre). */
  levelNextScore: number;
  /** Score de combat = PointBattle (partie active). */
  combatScore: number;
  /** Ordre du rang militaire courant (0 = aucun). */
  rankOrder: number;
  /** Libellé du rang courant (null si aucun). */
  rankLabel: string | null;
  /** Code image du rang (/images/ranks/), pour l'icône. */
  rankImageCode: string | null;
  /** Score de combat requis pour le rang courant (borne basse barre combat). */
  rankFloorScore: number;
  /** Score de combat requis pour le rang suivant (= borne basse si rang max). */
  rankNextScore: number;
}

/** Resume compact d'un pret banque actif pour le tooltip flouz. */
export interface ActiveLoanSummary {
  loanId: number;
  originPlanetId: number;
  originPlanetName: string | null;
  principal: number;
  remainingPrincipal: number;
  accruedInterest: number;
  /** Total cumule des interets deja rembourses sur ce pret. */
  interestRepaid: number;
  hourlyInterestPct: number;
}

/**
 * Stocks de ressources d'une planète du joueur (refonte V4.3, cf wiki §24.2).
 * - planetId : si fourni et possédé, on retourne ses stocks. Sinon, défaut =
 *   CurrentPlanetId du PlayerUniverse, à défaut la 1ère planète possédée (QG).
 * - universeId : optionnel, scope à un univers (sinon partie active la plus récente).
 */
export async function fetchResourceSummary(
  universeId?: number,
  planetId?: number,
): Promise<ResourceSummaryDto> {
  const params: Record<string, number> = {};
  if (universeId != null) params.universeId = universeId;
  if (planetId != null) params.planetId = planetId;
  const { data } = await apiClient.get<ResourceSummaryDto>(`${BASE}/resource-summary`, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return data;
}

/**
 * GET /api/v1/me/items — inventaire d'items du joueur (cf wiki §24.12).
 * Si universeId est omis, le backend selectionne la partie active la plus
 * recente du joueur. Items.quantity > 0, soft-deleted exclus.
 */
import type { MeItemsDto } from '@/types/inventory';

export async function fetchMyItems(universeId?: number): Promise<MeItemsDto> {
  const params: Record<string, number> = {};
  if (universeId != null) params.universeId = universeId;
  const { data } = await apiClient.get<MeItemsDto>(`${BASE}/items`, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return data;
}

/**
 * GET /api/v1/me/ship-components — P3-2 : inventaire des COMPOSANTS de vaisseau
 * LIBRES du joueur (lootés / récupérés au combat, non équipés), montables au
 * studio. Même résolution de scope que /me/items.
 */
import type { MeShipComponentsDto } from '@/types/shipComponent';

export async function fetchMyShipComponents(
  universeId?: number,
  includeBasics = false,
): Promise<MeShipComponentsDto> {
  const params: Record<string, number | boolean> = {};
  if (universeId != null) params.universeId = universeId;
  // Les composants BASIQUES (type IsFixed) sont exclus par défaut de l'inventaire
  // (ils vivent dans la palette du studio) ; on ne les demande que sur bascule.
  if (includeBasics) params.includeBasics = true;
  const { data } = await apiClient.get<MeShipComponentsDto>(`${BASE}/ship-components`, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return data;
}

// --- Synthèse des planètes possédées (tableau de bord d'accueil) -------------

export interface PlanetOverviewResource {
  resourceId: number;
  code: string;
  label: string;
  productionPerHour: number;
}
export interface PlanetOverviewBiome {
  biomeId: number;
  code: string;
  label: string;
  pct: number;
  color: string;
  imageCode?: string | null;
}
export interface PlanetOverviewEnv {
  solar: number;
  temperature: number;
  gravity: number;
  oxygen: number;
  co2: number;
  nitrogen: number;
}
export interface PlanetOverviewIdeals {
  idealSolar: number;
  idealTemperature: number;
  idealGravity: number;
  idealOxygen: number;
  idealCo2: number;
  idealNitrogen: number;
}
export interface PlanetOverviewItem {
  planetId: number;
  name: string;
  coordinates?: string | null;
  imageCode?: string | null;
  typeLabel?: string | null;
  isCapital: boolean;
  population: number;
  placesUsed: number;
  placesTotal: number;
  wellBeing: number;
  taxRate: number;
  flouzPerHour: number;
  biomes: PlanetOverviewBiome[];
  environment: PlanetOverviewEnv;
  raceIdeals?: PlanetOverviewIdeals | null;
  topResources: PlanetOverviewResource[];
}

/**
 * GET /api/v1/me/planets-overview — une ligne par planète possédée (stats utiles
 * pour la carte de planète du tableau de bord). Scope = universeId si fourni,
 * sinon la partie active la plus récente côté backend.
 */
export async function fetchPlanetsOverview(universeId?: number): Promise<PlanetOverviewItem[]> {
  const params: Record<string, number> = {};
  if (universeId != null) params.universeId = universeId;
  const { data } = await apiClient.get<PlanetOverviewItem[]>(`${BASE}/planets-overview`, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return data;
}
